import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Normalize timestamp to milliseconds (handles both seconds and milliseconds).
 * API returns Unix seconds (10-digit), JavaScript needs milliseconds (13-digit).
 *
 * @example
 * normalizeTimestamp(1704067200)    // Returns 1704067200000 (converted from seconds)
 * normalizeTimestamp(1704067200000) // Returns 1704067200000 (already in ms)
 */
export function normalizeTimestamp(timestamp: number): number {
  // If timestamp is less than year 2000 in ms, it's likely in seconds
  return timestamp < 946684800000 ? timestamp * 1000 : timestamp
}

/**
 * Get current time in Unix seconds.
 * Use this instead of `Math.floor(Date.now() / 1000)`.
 *
 * @example
 * const now = nowSeconds()
 * if (dueDate < now) { // overdue }
 */
export function nowSeconds(): number {
  return Math.floor(Date.now() / 1000)
}

/**
 * Convert Unix seconds timestamp to JavaScript Date object.
 * Use this instead of `new Date(timestamp * 1000)`.
 *
 * @example
 * const date = fromUnixSeconds(event.unix_start)
 * return date.toLocaleDateString()
 */
export function fromUnixSeconds(timestamp: number): Date {
  return new Date(normalizeTimestamp(timestamp))
}

/**
 * Convert JavaScript Date to Unix seconds timestamp.
 *
 * @example
 * const unix = toUnixSeconds(new Date())
 */
export function toUnixSeconds(date: Date): number {
  return Math.floor(date.getTime() / 1000)
}

/**
 * Get start of day (00:00:00) in Unix seconds for a given date.
 * Defaults to today if no date provided.
 *
 * @example
 * const startOfDay = startOfDaySeconds()
 * filter.unix_start = { $gte: startOfDay }
 */
export function startOfDaySeconds(date?: Date): number {
  const d = date ? new Date(date) : new Date()
  d.setHours(0, 0, 0, 0)
  return Math.floor(d.getTime() / 1000)
}

/**
 * Get end of day (23:59:59) in Unix seconds for a given date.
 * Defaults to today if no date provided.
 *
 * @example
 * const endOfDay = endOfDaySeconds()
 * filter.unix_start = { $lt: endOfDay }
 */
export function endOfDaySeconds(date?: Date): number {
  const d = date ? new Date(date) : new Date()
  d.setHours(23, 59, 59, 999)
  return Math.floor(d.getTime() / 1000)
}

/**
 * Check if a Unix seconds timestamp is overdue (before current time).
 *
 * @example
 * if (isOverdue(task.due_date)) {
 *   return <Badge variant="destructive">Overdue</Badge>
 * }
 */
export function isOverdue(timestamp: number): boolean {
  if (!timestamp || timestamp === 0) return false
  return timestamp < nowSeconds()
}

/**
 * Check if a Unix seconds timestamp falls within today.
 *
 * @example
 * if (isToday(event.unix_start)) {
 *   return <Badge>Today</Badge>
 * }
 */
export function isToday(timestamp: number): boolean {
  if (!timestamp) return false
  const start = startOfDaySeconds()
  const end = endOfDaySeconds()
  return timestamp >= start && timestamp <= end
}

/**
 * Format a timestamp as a human-readable date
 */
export function formatDate(timestamp: number): string {
  const date = new Date(normalizeTimestamp(timestamp))
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })
}

/**
 * Format a timestamp as a relative time string (e.g., "5 minutes ago")
 */
export function formatRelativeTime(timestamp: number): string {
  const normalizedTs = normalizeTimestamp(timestamp)
  const now = Date.now()
  const diff = now - normalizedTs
  const seconds = Math.floor(diff / 1000)
  const minutes = Math.floor(seconds / 60)
  const hours = Math.floor(minutes / 60)
  const days = Math.floor(hours / 24)

  if (seconds < 60) return "just now"
  if (minutes < 60) return `${minutes}m ago`
  if (hours < 24) return `${hours}h ago`
  if (days === 1) return "yesterday"
  if (days < 7) return `${days}d ago`

  return formatDate(timestamp)
}

/**
 * Format a duration in seconds as a human-readable string (e.g., "2:34")
 */
export function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}:${secs.toString().padStart(2, "0")}`
}

/**
 * Get initials from a name (e.g., "John Doe" -> "JD")
 */
export function getInitials(name: string | undefined | null): string {
  if (!name) return ""
  return name
    .split(" ")
    .map((part) => part.charAt(0))
    .join("")
    .toUpperCase()
    .slice(0, 2)
}

/**
 * Format a phone number for display (e.g., "(555) 123-4567")
 */
export function formatPhone(phone: string): string {
  const cleaned = phone.replace(/\D/g, "")
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`
  }
  if (cleaned.length === 11 && cleaned.startsWith("1")) {
    return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`
  }
  return phone
}

const WEEKDAY_INDEX: Record<string, number> = {
  Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6,
}

export function getWeekdayInTz(timestamp: number, timezone: string): number {
  const weekday = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    weekday: "short",
  }).format(fromUnixSeconds(timestamp))
  return WEEKDAY_INDEX[weekday] ?? 0
}

export function getWeekdayLabelInTz(timestamp: number, timezone: string): string {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    weekday: "long",
  }).format(fromUnixSeconds(timestamp))
}

export function isWeekendInTz(timestamp: number, timezone: string): boolean {
  const day = getWeekdayInTz(timestamp, timezone)
  return day === 0 || day === 6
}

export function formatDateInTz(timestamp: number, timezone: string): string {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(fromUnixSeconds(timestamp))
}

// Subtract N days and snap to 23:59:59.999 in the target timezone, return unix seconds.
export function shiftToFridayEndOfDayInTz(timestamp: number, timezone: string): number {
  const day = getWeekdayInTz(timestamp, timezone)
  const daysBack = day === 0 ? 2 : day === 6 ? 1 : 0
  if (!daysBack) return timestamp

  // Extract Y-M-D in the target timezone
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(fromUnixSeconds(timestamp))
  const y = parts.find((p) => p.type === "year")!.value
  const m = parts.find((p) => p.type === "month")!.value
  const d = parts.find((p) => p.type === "day")!.value

  // Build "{date} 23:59:59" as if it were UTC, then apply tz offset correction
  const asUtc = new Date(`${y}-${m}-${d}T23:59:59.999Z`)
  asUtc.setUTCDate(asUtc.getUTCDate() - daysBack)

  // Compute the offset between UTC and the target tz AT that instant, then correct
  const tzRender = new Date(asUtc.toLocaleString("en-US", { timeZone: timezone }))
  const utcRender = new Date(asUtc.toLocaleString("en-US", { timeZone: "UTC" }))
  const offsetMs = utcRender.getTime() - tzRender.getTime()

  return Math.floor((asUtc.getTime() + offsetMs) / 1000)
}

/**
 * Throttle function that runs at the end of a delay period.
 * If callTimes is set, runs immediately after that many calls OR after the delay.
 *
 * @param fn - Function to throttle
 * @param delay - Delay in milliseconds (default 250)
 * @param callTimes - Number of calls before running immediately (0 = always wait for delay)
 */
export function throttle<T extends (...args: any[]) => any>(
  fn: T,
  delay: number = 250,
  callTimes: number = 0
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null
  let calls = 0

  return (...args: Parameters<T>) => {
    if (timeout) {
      clearTimeout(timeout)
    }
    calls++

    if (callTimes > 0 && calls > callTimes) {
      fn(...args)
      calls = 0
    } else {
      timeout = setTimeout(() => {
        fn(...args)
        calls = 0
      }, delay)
    }
  }
}
